import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'form',
    'results',
    'searchInput',
    'loadingSpinner',
    'fieldInput',
    'textInput',
    'fieldCountHelper',
    'activeTermsContainer',
    'activeTerm',
    'activeSearchTerm',
    'count',
    'resetAllBtn',
    'copyContainer',
    'copyUrl',
    'timespanFieldPlaceholder',
  ];

  static values = {
    ajaxUrl: String,
    count: Number,
  };

  connect() {
    this.formTarget.addEventListener('submit', (event) => {
      event.preventDefault();
    });

    // Todo: Add LoadMore
    this.formData = new FormData(this.formTarget);

    this.ajaxUrlLang = document.documentElement.lang == 'en-US' ? '/en' : '';

    this.ajaxUrl = this.hasAjaxUrlValue ? this.ajaxUrlValue : '/ajax/entries';

    this.debounceTimeout = null;
    this.isLoading = false;

    this.queryString = this.buildQueryString();

    // Listen for filter changes
    this.element.addEventListener('filter:apply', this.applyFilter.bind(this));
    // Listen for Search Updates
    this.element.addEventListener('search:update', this.updateSearch.bind(this));

    if (this.hasTimespanFieldPlaceholderTarget) {
      this.addTimeSpanFields();
    }

    // Listen for the loadMore
    // this.element.addEventListener(
    //   'loadmore:loadMore',
    //   this.loadMore.bind(this)
    // );
  }

  async updateSearch() {
    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(async () => {
      this.updateFormData();

      this.clearResultsContainer();
      this.showLoadingSpinner();
      const data = await this.fetchResults();
      this.updateUrl();
      this.populateResultsContainer(data);
      this.updateFilterCount();
      this.hideLoadingSpinner();
      this.updateActiveTerms();

      this.setResultsCountValue(this.getResultsCount());
      this.updateResultsCount();

      // Check if search active
      // this.showActiveTerms();
      // this.showResetBtn();
      this.updateCopyUrl();
    }, 300);
  }

  async applyFilter() {
    this.updateFormData();

    this.clearResultsContainer();
    this.showLoadingSpinner();
    const data = await this.fetchResults();
    this.updateUrl();
    this.populateResultsContainer(data);
    this.hideLoadingSpinner();
    this.updateFilterCount();
    this.updateActiveTerms();

    this.setResultsCountValue(this.getResultsCount());
    this.updateResultsCount();

    this.updateCopyUrl();
  }

  updateFilterCount() {
    // Lookup Map from temp elements with data-count attributes
    const dataCountMap = {};
    this.fieldCountHelperTargets.forEach((element) => {
      const dataId = element.getAttribute('data-id');
      const dataCount = element.getAttribute('data-count');
      dataCountMap[dataId] = dataCount; // Store in a map (key: data-id, value: data-count)
    });

    // Update the matching elements from form fields
    this.fieldInputTargets.forEach((input) => {
      const inputValue = input.value;
      let dataCount = dataCountMap[inputValue];

      const isTimeSpan = input.closest('[data-timespan]') ? true : false;
      if (isTimeSpan) {
        dataCount = dataCountMap['custom'];
      }
      if (dataCount === undefined) {
        dataCount = 0; // Treat undefined as 0
      }

      if (!isTimeSpan) {
        input.disabled = dataCount == 0;
      }

      let countEl = input.closest('label')?.querySelector('[data-count-el]');

      // Check to get count el of timespan field
      if (countEl === null || isTimeSpan) {
        const container = input.closest('[data-timespan]');
        countEl = container?.querySelector('[data-count-el]');
      }

      if (countEl) {
        countEl.textContent = `(${dataCount})`;
      }
    });

    // Remove temp elements for data-count -> maybe move to a data attrbibute
    this.fieldCountHelperTargets.forEach((el) => el.remove());
  }

  async fetchResults() {
    try {
      this.queryString = this.buildQueryString();

      const response = await fetch(`${this.ajaxUrlLang}${this.ajaxUrl}?${this.queryString}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }

      const data = response.text();

      return data;
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  }

  buildQueryString() {
    const params = new URLSearchParams();
    // Iterate over the formData entries and add them to the URLSearchParams
    for (const [key, value] of this.formData.entries()) {
      if (params.has(key)) {
        params.append(key, value); // Append value if the key already exists (multiple values allowed)
      } else {
        params.set(key, value); // Set value if the key doesn't exist yet
      }
    }

    // Return the query string
    return params.toString();
  }

  populateResultsContainer(html) {
    this.resultsTarget.insertAdjacentHTML('beforeend', html);
  }

  clearResultsContainer() {
    this.resultsTarget.innerHTML = ''; // Clear all existing results
  }

  updateUrl() {
    if (this.queryString == '') {
      const url = new URL(window.location.href);
      // Remove all parameters
      url.search = '';
      // Update the browser's URL without reloading
      window.history.replaceState(null, '', url);
    } else {
      // Update the URL with the new query string using history.pushState
      const newUrl = `${window.location.pathname}?${this.queryString}`;
      window.history.pushState({ path: newUrl }, '', newUrl);
    }
  }

  showLoadingSpinner() {
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.style.display = 'block';
    }
  }

  hideLoadingSpinner() {
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.style.display = 'none';
    }
  }

  updateFormData() {
    this.formData = new FormData(this.formTarget);
  }

  updateActiveTerms() {
    this.updateActiveSearchTerm();
    this.updateActiveFilterTerms();
  }

  updateActiveFilterTerms() {
    // Create a new FormData object to hold the filtered values
    const filteredFormData = new FormData();

    // Iterate over the entries of the original formData and add them to the new object
    for (const [key, value] of this.formData.entries()) {
      // Skip the 'sections' key
      if (key !== 'type') {
        filteredFormData.append(key, value);
      }
    }

    // Create a Set of the values from filteredFormData for quick lookup
    const formDataValues = new Set(
      [...filteredFormData.values()].filter((value) => value !== '' && value !== null && value !== undefined)
    );

    // Iterate through all activeTermTargets
    this.activeTermTargets.forEach((target) => {
      // Check if the target's data-id exists in the Set of filtered formData values
      if (formDataValues.has(target.dataset.id)) {
        // If the value is present, remove the 'hidden' class
        target.classList.remove('hidden');
      } else {
        // Otherwise, add the 'hidden' class
        target.classList.add('hidden');
      }
    });

    if (formDataValues.size > 0) {
      this.showActiveTerms();
    } else {
      this.hideActiveTerms();
    }
  }

  updateActiveSearchTerm() {
    if (this.hasActiveSearchTermTarget) {
      const valueTarget = this.activeSearchTermTarget.querySelector('[data-value]');
      const searchValue = this.formData.get('search');
      if (!searchValue) {
        this.removeActiveTerm(this.activeSearchTermTarget);
      } else {
        this.activeSearchTermTarget.classList.remove('hidden');
        valueTarget.textContent = searchValue;
      }
    }
  }

  hideActiveTerms() {
    if (this.hasActiveTermsContainerTarget) {
      this.activeTermsContainerTarget.classList.add('hidden');
    }
    this.hideResetBtn();
  }
  showActiveTerms() {
    if (this.hasActiveTermsContainerTarget) {
      this.activeTermsContainerTarget.classList.remove('hidden');
    }
    this.showResetBtn();
  }

  removeSearch() {
    this.removeActiveTerm(this.activeSearchTermTarget);
    this.resetSearchInput();
    this.updateSearch();
  }

  removeField(e) {
    const target = e.target.closest('[data-entries-target="activeTerm"]');
    if (target) {
      this.removeActiveTerm(target);
      this.removeValueFromForm(target.dataset.id);
    }
  }

  removeActiveTerm(item) {
    item.classList.add('hidden');
  }

  removeValueFromForm(inputValue) {
    const input = this.formTarget.querySelector(`input[value="${inputValue}"]`);

    if (input && input.type == 'radio') {
      input.checked = false;
      this.applyFilter();
    } else {
      input.click();
    }
  }

  resetSearchInput() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = '';
    }
  }

  setResultsCountValue(newCount) {
    this.countValue = newCount;
  }

  getResultsCount() {
    const resultsCountEl = document.querySelector('[data-results-count]');

    if (!resultsCountEl) {
      return 0;
    }
    return resultsCountEl.dataset.resultsCount;
  }

  updateResultsCount() {
    if (this.hasCountTarget) {
      this.countTarget.innerHTML = this.countValue;
    }
  }

  async resetAll() {
    this.resetForm();
    this.updateFormData();

    this.clearResultsContainer();
    this.showLoadingSpinner();
    const data = await this.fetchResults();
    this.updateUrl();
    this.populateResultsContainer(data);
    this.updateFilterCount();
    this.hideLoadingSpinner();
    this.updateActiveTerms();

    this.setResultsCountValue(this.getResultsCount());
    this.updateResultsCount();

    this.hideActiveTerms();
    this.resetCopyUrl();
  }

  resetForm() {
    this.resetSearchInput();
    this.resetFields();
  }

  resetFields() {
    this.fieldInputTargets.forEach((input) => {
      input.checked = false;
    });
    if (this.hasTextInputTarget) {
      this.textInputTargets.forEach((input) => {
        input.value = '';
      });
    }
  }

  updateCopyUrl() {
    if (this.hasCopyContainerTarget) {
      this.copyContainerTarget.classList.remove('hidden');
    }
    if (this.hasCopyUrlTarget) {
      this.copyUrlTarget.textContent = window.location.href;
    }
  }
  resetCopyUrl() {
    if (this.hasCopyContainerTarget) {
      this.copyContainerTarget.classList.add('hidden');
    }
    if (this.hasCopyUrlTarget) {
      this.copyUrlTarget.textContent = '';
    }
  }

  showResetBtn() {
    if (this.hasResetAllBtnTarget) {
      this.resetAllBtnTarget.classList.remove('hidden');
    }
  }

  hideResetBtn() {
    if (this.hasResetAllBtnTarget) {
      this.resetAllBtnTarget.classList.add('hidden');
    }
  }

  addTimeSpanFields() {
    this.timespanFieldPlaceholderTargets.forEach((placeholder) => {
      const title = placeholder.dataset.title;
      const name = placeholder.dataset.name;
      const startDateValue = placeholder.dataset.startDate;
      const endDateValue = placeholder.dataset.endDate;

      const relationcount = placeholder.dataset.relationcount;
      const checked = placeholder.dataset.checked;
      const id = placeholder.dataset.id;
      const toWording = placeholder.dataset.wording ?? 'bis';

      const container = document.createElement('div');

      const template = `
        <div class="flex flex-col gap-4" data-timespan>
          <label class="flex items-center gap-2 cursor-pointer filter-label filter-radio">
		        <input data-entries-target="fieldInput" data-action="click->filter#apply" type="radio" name="${name}" data-title="${title}" value="${id}" data-timespan-value class="hidden" ${
        checked ? 'checked' : ''
      } >
            <span class="flex items-center justify-center flex-shrink-0 border rounded-full filter-mark size-6 border-seagreen-300">
            <div class="hidden bg-seagreen-900 size-3.5 rounded-full"></div>
            </span>
            <span class="text-left">${title}</span>
	        </label>

          <label class="flex justify-between gap-2 pl-8 items-center">
            <div class="flex gap-2 items-center" data-timespan-inputs>
              <input data-entries-target="textInput" type="input" maxlength="4" pattern="\d{4}" placeholder="2015" data-timespan-input name="start-date" value="${startDateValue}" class="filter-text-input">
              <span class="t-interaction--lg">${toWording}</span>
              <input data-entries-target="textInput" type="input" maxlength="4" pattern="\d{4}" placeholder="2025" data-timespan-input name="end-date" value="${endDateValue}" class="filter-text-input">
            </div>
            <span class="ml-auto font-normal text-black-off flex-shrink-0" data-count-el>(${relationcount})</span>
          </label>
        </div>
      `;
      container.innerHTML = template;
      placeholder.appendChild(container);
      placeholder.removeAttribute('hidden');
    });
    const customTimespanInputs = document.querySelectorAll('[data-timespan-input]');
    Array.from(customTimespanInputs).forEach((input) => {
      input.addEventListener('keydown', (event) => this.suppressNonNumericInput(event));
      input.addEventListener('input', (event) => {
        this.handleTimeSpanInput(input, event);
      });
    });
  }

  // Handle input for both fields
  handleTimeSpanInput(input, event) {
    const container = input.closest('[data-timespan-inputs]');
    const areBothValid = this.validateTimeSpanInputs(container);
    if (areBothValid) {
      // input.classList.remove('border-orange', 'border');
      input.setCustomValidity('');
      // activate timespan option and trigger applyFilter
      const parentContainer = container.closest('[data-timespan]');
      const timespanValueInput = parentContainer.querySelector('[data-timespan-value]');
      timespanValueInput.checked = true;
      this.applyFilter();
    } else {
      // Todo: reset empty time input to empty
      // input.classList.add('border-orange', 'border');
      input.setCustomValidity('Please enter a valid 4-digit year');
    }
  }

  setTimeSpanField(container, newValue) {
    const parentContainer = container.closest('[data-timespan]');
    const timespanValueInput = parentContainer.querySelector('[data-timespan-value]');
    timespanValueInput.value = newValue;
  }
  // Validation logic
  validateTimeSpanInput(input) {
    const value = input.value;

    // Check if value is numeric and 4 characters long
    const isValid = /^\d{4}$/.test(value);

    return isValid;
  }
  // Validation logic for both inputs together
  validateTimeSpanInputs(container) {
    const inputs = container.querySelectorAll('[data-timespan-input]');
    let allValid = true;

    inputs.forEach((input) => {
      const isValid = this.validateTimeSpanInput(input);
      if (!isValid) {
        allValid = false; // If any input is invalid, set allValid to false
      }
    });

    return allValid;
  }
  // Suppress non-numeric input
  suppressNonNumericInput(event) {
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'];

    // Allow numeric keys, navigation keys, and editing keys
    if (!/^\d$/.test(event.key) && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  // Not Used atm but useful

  // async loadMore() {
  //   // Increment the page number and fetch more results
  //   this.page += 1;
  //   const data = await this.fetchResults();
  //   this.updateUrl();
  //   this.populateResultsContainer(data);
  // }
  // resetUrl() {
  //   const url = new URL(window.location.href);
  //   // Remove all parameters
  //   url.search = '';
  //   // Update the browser's URL without reloading
  //   window.history.replaceState(null, '', url);
  // }
}
